import { EntityTypes } from 'models/entity';
import { TenantProcessStateInsight } from 'models/copilot/Processes/Insight/tenantProcessStateInsight';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { TenantProcessStateBaseDTO } from 'models/dto/copilot/tenantProcessStateBaseDTO';
import {
  InsightActionDTO,
  InsightCitationDTO,
  InsightDTO,
  InsightSentenceDTO,
  InsightTargetDTO,
  TenantProcessStateInsightDTO,
} from 'models/dto/insightDTO';
import { Insight, InsightAction, InsightCitation, InsightSentence, InsightTarget } from 'models/insight';
import {
  parseInsightActionKind,
  parseInsightCitationKind,
  parseInsightSource,
  parseInsightTargetPanel,
} from 'models/projects/projectEnums';
import { fromApiDateTimeOptional } from 'utils/datetime';

//
// AIInsight <-> Insight. A word this client cannot name is dropped, not defaulted: a citation kind
// decides the whole chip and an action kind the whole button, so an unnameable one cannot be
// rendered at all. `mapFromInsight` is what every caller uses, so the drop happens in one place.
//

function mapFromInsightSentence(dto: InsightSentenceDTO): InsightSentence {
  const sentence = new InsightSentence(dto.text ?? '');
  sentence.citationKeys = dto.citationKeys ?? [];

  return sentence;
}

function mapFromInsightCitation(dto: InsightCitationDTO): InsightCitation | undefined {
  const kind = parseInsightCitationKind(dto.kind);
  if (kind === undefined || dto.key === undefined) return undefined;

  // The API writes an absent field as null; the model knows absence as undefined only.
  const citation = new InsightCitation(dto.key, kind, dto.marker ?? 0);
  citation.isoControlId = dto.isoControlId ?? undefined;
  citation.entityType = dto.entityType ?? undefined;
  citation.entityId = dto.entityId ?? undefined;
  citation.linkId = dto.linkId ?? undefined;
  citation.version = dto.version ?? undefined;
  citation.roomKey = dto.roomKey ?? undefined;
  citation.taskId = dto.taskId ?? undefined;
  citation.value = dto.value ?? undefined;

  return citation;
}

function mapFromInsightTarget(dto: InsightTargetDTO): InsightTarget {
  const target = new InsightTarget();
  target.entityType = dto.entityType ?? undefined;
  target.entityId = dto.entityId ?? undefined;
  target.roomKey = dto.roomKey ?? undefined;
  target.taskId = dto.taskId ?? undefined;
  target.panel = dto.panel ? parseInsightTargetPanel(dto.panel) : undefined;

  return target;
}

function mapFromInsightAction(dto: InsightActionDTO): InsightAction | undefined {
  const kind = parseInsightActionKind(dto.kind);
  if (kind === undefined) return undefined;

  const action = new InsightAction(kind);
  action.primary = dto.primary === true;
  action.target = dto.target ? mapFromInsightTarget(dto.target) : undefined;
  action.reasonKey = dto.reasonKey ?? undefined;
  action.reasonValue = dto.reasonValue ?? undefined;

  return action;
}

/** One composed answer. Returns undefined when the wire names a source this client cannot place. */
export function mapFromInsight(dto: InsightDTO): Insight | undefined {
  const source = parseInsightSource(dto.source);
  if (source === undefined) return undefined;

  const insight = new Insight(source);
  insight.failureReason = dto.failureReason ?? undefined;
  insight.sentences = (dto.sentences ?? []).map(mapFromInsightSentence);
  insight.citations = (dto.citations ?? [])
    .map(mapFromInsightCitation)
    .filter((citation): citation is InsightCitation => citation !== undefined);
  insight.actions = (dto.actions ?? [])
    .map(mapFromInsightAction)
    .filter((action): action is InsightAction => action !== undefined);
  insight.generatedAt = fromApiDateTimeOptional(dto.generatedAt);
  insight.stateHash = dto.stateHash ?? undefined;

  return insight;
}

/** The whole process row, as the execute call returns it. */
export function mapFromTenantProcessStateInsight(item: TenantProcessStateBaseDTO): TenantProcessStateInsight {
  const data = JSON.parse(item.data || '{}') as TenantProcessStateInsightDTO;
  const output = new TenantProcessStateInsight();
  output.focus = data.focus ?? '';
  output.force = data.force === true;

  Object.entries(data.insights ?? {}).forEach(([key, value]) => {
    const insight = mapFromInsight(value);
    if (insight !== undefined) output.insights.set(key, insight);
  });

  return output;
}

/** The request half only: the server fills the answers. */
export function mapToTenantProcessStateInsight(
  entityType: EntityTypes,
  entityId: number,
  focus: string,
  force: boolean,
): TenantProcessStateBaseDTO {
  const data = new TenantProcessStateInsightDTO();
  data.focus = focus;
  data.force = force;

  const output = new TenantProcessStateBaseDTO();
  output.processId = TenantProcess.Insight;
  output.typeOfEntity = entityType;
  output.entityId = entityId;
  output.status = TenantProcessStatus.NotStarted;
  output.data = JSON.stringify(data);

  return output;
}
